/**
 * 非同期ジョブAPI クライアント
 *
 * 画像生成、動画生成、AI俳優生成などの長時間処理を
 * バックグラウンドで実行し、ポーリングで結果を取得するためのAPI
 */
import { apiClient } from './apiClient';
import type { MotionModel } from '../features/motion/model';
import { waitForNextPoll } from '../utils/polling';

/**
 * Seedance 実在人物フィルタが指した原因素材 (backend face_privacy が失敗ジョブの
 * result_data.face_privacy_flagged に書き込む)。FE は kind / url で該当 UI 要素
 * (ロゴ・商品画像チェックボックスや絵コンテフレーム・選択素材カード) を赤枠表示する。
 */
export interface FacePrivacyFlaggedItem {
  kind: string;
  label?: string;
  url?: string;
  role?: string;
  content_index?: number;
}

// 非同期ジョブのエラー（error_codeを保持）
export class AsyncJobError extends Error {
  error_code?: string;
  /** Seedance 実在人物フィルタで失敗したとき、原因素材の一覧 */
  face_privacy_flagged?: FacePrivacyFlaggedItem[];
  /**
   * 失敗したジョブの result_data (あれば)。ジョブが**失敗しても消えない**情報を呼び出し側が
   * 拾うためのもの。例: 動画エディタの AI ジョブは、実行前に project 行へ書いた導出値と
   * 新しい revision を `projectPatch` として残す (失敗時もフロントは revision を合わせ直す
   * 必要がある。docs/features/video-editor-capability-gaps-plan.md §4.3-T)。
   */
  result_data?: Record<string, unknown>;
  constructor(
    message: string,
    error_code?: string,
    face_privacy_flagged?: FacePrivacyFlaggedItem[],
    result_data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'AsyncJobError';
    this.error_code = error_code;
    this.face_privacy_flagged = face_privacy_flagged;
    this.result_data = result_data;
  }
}

// ジョブステータス
// cancelled: キャンセル確定済み。通常はジョブ行ごと削除されるため見えないが、
// デポジットの返金が確定しなかった場合は返金対象を失わないよう行を残すので、
// ポーリング中のクライアントがこの状態を観測しうる。
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

// サービスタイプ
export type ServiceType = 'image_generation' | 'video_generation' | 'actor_generation' | 'canva_upload' | 'product_upload' | 'scene_generation' | 'concat_video' | 'bgm_generation' | 'video_edit';

// ジョブ情報
export interface AsyncJob {
  id: string;
  service_type: ServiceType;
  job_type: string;
  status: JobStatus;
  progress?: number;
  /** 実行中の段階 (backend progress.report_stage)。生成キューがラベルの下に出す。処理中の行にだけ付く */
  stage?: { text?: string; detail?: string; at?: string } | null;
  result_data?: Record<string, unknown>;
  error_message?: string;
  error_code?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  /** 実行中の生成イベント通知: 顔フィルタ回避の覆面化 (事前サニタイズ) が起きたとき (動画生成のみ) */
  face_privacy_notice?: { count?: number };
  /** failed 時: Seedance 実在人物フィルタが指した原因素材の一覧 */
  face_privacy_flagged?: { items?: FacePrivacyFlaggedItem[] };
  /**
   * 動画エディタの自動編集 (video_edit autopilot) が processing 中に載せる進捗。
   * 型は videoEditor/api.ts の AutopilotProgress (循環 import を避けるためここでは緩い型にする。
   * 利用側 (hooks/useAutopilotRun.ts) で as AutopilotProgress にキャストする)。
   */
  autopilot?: Record<string, unknown>;
  /**
   * 動画エディタの AI ジョブが**実行前に** project 行へ書いた導出値と、その結果の新しい
   * revision (processing / failed の両方で載る)。フロントは project を読み直さないので、
   * これを取り込まないと次の保存が 409 になる
   * (docs/features/video-editor-capability-gaps-plan.md §4.3-T)。
   * 型は videoEditor/api.ts の VideoEditProjectPatch (循環 import を避けて緩い型で持つ)。
   */
  projectPatch?: Record<string, unknown>;
}

// ジョブ作成レスポンス
export interface CreateJobResponse {
  job_id: string;
  status: JobStatus;
}

/**
 * ジョブ作成〜完了待ちを行う API 共通のオプション。
 * signal を渡すと HTTP リクエストとポーリングの両方が中断され、AbortError が throw される。
 */
export interface JobWaitOptions {
  onProgress?: (job: AsyncJob) => void;
  signal?: AbortSignal;
}

/** abort 済みなら AbortError を throw する (呼び出し側は err.name === 'AbortError' で判定できる) */
function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}

/**
 * abort 由来のエラーか判定する。
 * throwIfAborted の DOMException(AbortError) と axios の CanceledError (code: ERR_CANCELED) の両方を拾う。
 */
export function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: string; code?: string };
  return err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED';
}

/** abort で即座に解決する sleep (reject はしない。待機明けに throwIfAborted で判定する) */
function sleepUntilAborted(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    if (signal?.aborted) {
      done();
      return;
    }
    signal?.addEventListener('abort', done);
  });
}

// 人物画像生成パラメータ
export interface GeneratePersonParams {
  prompt: string;
  model?: string;
  gender?: string;
  age_min?: number;
  age_max?: number;
  ethnicity?: string;
  body_type?: string;
  physical_features?: string;
}

// キャラクターシート生成パラメータ
// 元画像は source_material_id (人物生成の結果素材) か source_image_base64 (アップロード画像。
// バックエンドが enqueue 時に一時素材へ変換する) のどちらかで渡す。
export interface GenerateCharacterSheetParams {
  source_material_id?: string;
  source_image_base64?: string;
  additional_prompt?: string;
  model?: string;
}

// 名前提案パラメータ
export interface SuggestNameAttributes {
  gender?: 'male' | 'female';
  age_min?: number;
  age_max?: number;
  body_type?: string;
  physical_features?: string;
}

export interface SuggestNameParams {
  /** 省略可。人物生成の結果素材を参照する (アップロード画像は image_base64 で送る) */
  image_material_id?: string;
  /** 省略可。人物画像の生成完了を待たずに先読みする場合は送らない */
  image_base64?: string;
  ethnicity?: string;
  generation_prompt?: string;
  attributes?: SuggestNameAttributes;
}

// 人物画像生成結果 (ジョブの result_data)。
// 画像は material_id で返り、base64 は含まれない (docs/features/async-jobs-bloat-cleanup-plan.md A-1)。
// image_base64 は旧バックエンド (互換期間) の応答にだけ入る。
export interface GeneratePersonResult {
  material_id?: string;
  image_base64?: string;
  mime_type: string;
  prompt_used: string;
  credits_consumed: number;
}

// キャラクターシート生成結果 (同上)
export interface GenerateCharacterSheetResult {
  material_id?: string;
  image_base64?: string;
  mime_type: string;
  credits_consumed: number;
}

/** AI俳優の画像ジョブの結果を、ウィザードが扱う base64 の形にそろえたもの */
export interface ActorImageJobResult {
  image_base64: string;
  mime_type: string;
  /** 生成画像の素材 ID。次工程 (シート生成・名前提案) はこれを参照で渡す。旧バックエンドの応答では null */
  material_id: string | null;
}

/** base64 の data URL (またはバイナリ Blob) をウィザード用の base64 文字列へ */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('画像の読み込みに失敗しました'));
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * AI俳優の画像ジョブ (人物生成 / キャラクターシート生成) の生成画像を取得する。
 * GET /api/jobs/{id}/result-image はジョブの所有者で認可し、素材をジョブの組織 (課金先) で読む。
 */
export async function fetchJobResultImage(jobId: string): Promise<{ base64: string; mimeType: string }> {
  const { data, headers } = await apiClient.get<Blob>(`/jobs/${jobId}/result-image`, {
    responseType: 'blob',
  });
  const mimeType = (headers?.['content-type'] as string | undefined)?.split(';')[0]
    || data.type
    || 'image/png';
  return { base64: await blobToBase64(data), mimeType };
}

/**
 * 画像ジョブの完了応答から、ウィザードが使う base64 を得る。
 * 新バックエンド (material_id のみ) は結果画像エンドポイントで取得し、
 * 旧バックエンド (image_base64 入り) はそのまま使う (互換期間の二重対応)。
 */
export async function resolveActorImageJobResult(
  job: AsyncJob & { result_data: { material_id?: string; image_base64?: string; mime_type: string } },
): Promise<ActorImageJobResult> {
  const rd = job.result_data;
  if (rd.image_base64) {
    return { image_base64: rd.image_base64, mime_type: rd.mime_type, material_id: rd.material_id ?? null };
  }
  if (!rd.material_id) {
    throw new Error('生成結果に画像がありません');
  }
  const { base64, mimeType } = await fetchJobResultImage(job.id);
  return { image_base64: base64, mime_type: rd.mime_type || mimeType, material_id: rd.material_id };
}

// 名前提案結果
export interface SuggestNameResult {
  name_suggestions: Array<{
    display_name: string;
    display_name_en?: string;
  }>;
  tags: string[];
}

/**
 * ジョブのステータスを取得
 */
export async function getJobStatus(jobId: string, signal?: AbortSignal): Promise<AsyncJob> {
  const response = await apiClient.get<AsyncJob>(`/jobs/${jobId}`, { signal });
  return response.data;
}

/**
 * ジョブ一覧を取得
 */
export async function listJobs(params?: {
  service_type?: ServiceType;
  status?: JobStatus;
  limit?: number;
}): Promise<AsyncJob[]> {
  const response = await apiClient.get<{ jobs: AsyncJob[] }>('/jobs', { params });
  return response.data.jobs;
}

/**
 * 人物画像生成ジョブを作成
 */
export async function createGeneratePersonJob(
  params: GeneratePersonParams
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/actor/generate-person',
    params
  );
  return response.data;
}

/**
 * キャラクターシート生成ジョブを作成
 */
export async function createGenerateCharacterSheetJob(
  params: GenerateCharacterSheetParams
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/actor/generate-character-sheet',
    params
  );
  return response.data;
}

/**
 * 名前提案ジョブを作成
 */
export async function createSuggestNameJob(
  params: SuggestNameParams,
  signal?: AbortSignal
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/actor/suggest-name',
    params,
    { signal }
  );
  return response.data;
}

// ポーリング設定
const DEFAULT_POLL_INTERVAL = 2000; // 2秒
const MAX_POLL_DURATION = 30 * 60 * 1000; // 30分
const MAX_NETWORK_RETRIES = 8; // ネットワークエラー時の最大リトライ回数
const NETWORK_RETRY_BASE_DELAY = 1000; // リトライ基本間隔（指数バックオフ）
const NETWORK_RETRY_MAX_DELAY = 30000; // リトライ最大間隔

/**
 * ネットワークエラー時のリトライ付きでジョブステータスを取得
 */
async function getJobStatusWithRetry(
  jobId: string,
  maxRetries: number = MAX_NETWORK_RETRIES,
  signal?: AbortSignal
): Promise<AsyncJob> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    throwIfAborted(signal);
    try {
      return await getJobStatus(jobId, signal);
    } catch (error) {
      // abort 由来のエラー (axios の CanceledError 等) はリトライせず即中断する
      throwIfAborted(signal);
      lastError = error as Error;
      console.warn(`[Job ${jobId}] Network error on attempt ${attempt + 1}/${maxRetries}:`, error);

      // 最後の試行でなければ待機してリトライ
      if (attempt < maxRetries - 1) {
        const delay = Math.min(NETWORK_RETRY_BASE_DELAY * Math.pow(2, attempt), NETWORK_RETRY_MAX_DELAY);
        await sleepUntilAborted(delay, signal);
      }
    }
  }

  throwIfAborted(signal);
  throw lastError || new Error('Failed to get job status after retries');
}

/**
 * ページが非表示から表示に戻ったときに解決するPromiseを返す
 */
function waitForPageVisible(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (document.visibilityState === 'visible' || signal?.aborted) {
      resolve();
      return;
    }

    const handleAbort = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      resolve();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        signal?.removeEventListener('abort', handleAbort);
        // 少し待ってからネットワーク接続が安定するのを待つ
        setTimeout(resolve, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    signal?.addEventListener('abort', handleAbort);
  });
}

/**
 * ジョブの完了をポーリングで待機
 * スマホスリープ復帰時にも対応
 */
export async function waitForJobCompletion<T = Record<string, unknown>>(
  jobId: string,
  options?: {
    pollInterval?: number;
    maxDuration?: number;
    onProgress?: (job: AsyncJob) => void;
    /** ポップアップウィンドウを開く処理など、ページが非表示でもポーリングを継続したい場合にtrueを設定 */
    skipVisibilityCheck?: boolean;
    /** 呼び出し側からポーリングを中断する。abort 時は AbortError を throw する */
    signal?: AbortSignal;
  }
): Promise<AsyncJob & { result_data: T }> {
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL;
  const maxDuration = options?.maxDuration ?? MAX_POLL_DURATION;
  const skipVisibilityCheck = options?.skipVisibilityCheck ?? false;
  const signal = options?.signal;
  const startTime = Date.now();

  while (true) {
    throwIfAborted(signal);

    // ページが非表示の場合は表示されるまで待機（skipVisibilityCheckがfalseの場合のみ）
    if (!skipVisibilityCheck && document.visibilityState === 'hidden') {
      await waitForPageVisible(signal);
      throwIfAborted(signal);
    }

    try {
      // ネットワークエラー時はリトライ付きで取得
      const job = await getJobStatusWithRetry(jobId, MAX_NETWORK_RETRIES, signal);

      // 進捗コールバック
      if (options?.onProgress) {
        options.onProgress(job);
      }

      // 完了またはエラー
      if (job.status === 'completed') {
        return job as AsyncJob & { result_data: T };
      }

      if (job.status === 'failed') {
        throw new AsyncJobError(
          job.error_message || 'Job failed',
          job.error_code,
          job.face_privacy_flagged?.items,
          job.result_data,
        );
      }

      // cancelled も終端。返金が確定しなかったキャンセルではジョブ行が
      // cancelled のまま残るため、ここで止めないとタイムアウトまで回り続ける。
      if (job.status === 'cancelled') {
        throw new AsyncJobError('ジョブはキャンセルされました', 'JOB_CANCELLED');
      }

      // タイムアウトチェック（経過時間ベース、スリープ時間は除外）
      if (Date.now() - startTime > maxDuration) {
        throw new Error('Job polling timeout');
      }

    } catch (error) {
      // abort 由来のエラーはネットワークエラー扱いにせず即座に中断する
      throwIfAborted(signal);

      // ネットワークエラーの場合は再試行可能なので、少し待ってから継続
      const isNetworkError = error instanceof Error && (
        error.message.includes('Network') ||
        error.message.includes('fetch') ||
        error.message.includes('Failed to get job status')
      );

      if (isNetworkError) {
        // ネットワークエラーでもタイムアウトチェックを行う
        if (Date.now() - startTime > maxDuration) {
          throw new Error('Job polling timeout (network errors persisted)');
        }
        console.warn(`[Job ${jobId}] Network error, will retry after delay:`, error);
        await waitForNextPoll(pollInterval, signal);
        continue;
      }

      // その他のエラーは再スロー
      throw error;
    }

    // 次のポーリングまで待機
    await waitForNextPoll(pollInterval, signal);
  }
}

/**
 * 人物画像を生成（非同期ジョブとして実行し、完了まで待機）。
 * 戻り値の画像は結果画像エンドポイントから取得した base64 (旧バックエンドなら応答の base64)。
 */
export async function generatePersonAsync(
  params: GeneratePersonParams,
  options?: {
    onProgress?: (job: AsyncJob) => void;
  }
): Promise<ActorImageJobResult & { prompt_used: string }> {
  const { job_id } = await createGeneratePersonJob(params);
  const job = await waitForJobCompletion<GeneratePersonResult>(job_id, {
    onProgress: options?.onProgress,
  });
  const image = await resolveActorImageJobResult(job);
  return { ...image, prompt_used: job.result_data.prompt_used };
}

/**
 * キャラクターシートを生成（非同期ジョブとして実行し、完了まで待機）
 */
export async function generateCharacterSheetAsync(
  params: GenerateCharacterSheetParams,
  options?: {
    onProgress?: (job: AsyncJob) => void;
  }
): Promise<ActorImageJobResult> {
  const { job_id } = await createGenerateCharacterSheetJob(params);
  const job = await waitForJobCompletion<GenerateCharacterSheetResult>(job_id, {
    onProgress: options?.onProgress,
  });
  return resolveActorImageJobResult(job);
}

/**
 * 名前を提案（非同期ジョブとして実行し、完了まで待機）
 */
export async function suggestNameAsync(
  params: SuggestNameParams,
  options?: JobWaitOptions
): Promise<SuggestNameResult> {
  const { job_id } = await createSuggestNameJob(params, options?.signal);
  const job = await waitForJobCompletion<SuggestNameResult>(job_id, {
    onProgress: options?.onProgress,
    signal: options?.signal,
  });
  return job.result_data;
}

// ============================================
// 動画生成
// ============================================

// 動画生成結果
export interface VideoGenerationResult {
  material_id: string;
  video_url: string | null;
  thumbnail_url: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
  mime_type: string;
  scene_id: string | null;
  /** 顔フィルタ回避の覆面化リトライが起きた場合の情報 (起きなければ null/undefined) */
  face_privacy_sanitized?: { count?: number } | null;
  /** 尺の実測精算の結果 (backend async_job_service が completed 遷移後の別更新で書く。
   *  完了検知時点では未書き込みのことがあるため、FE は無ければ数秒後に 1 回だけ
   *  再取得して読む)。refunded_points > 0 なら「自動」尺の過大控除分が返還済み。 */
  _video_settlement?: {
    status?: string;
    actual_total_seconds?: number;
    refunded_points?: number;
    extra_charged_points?: number;
    deposited_points?: number;
    net_points?: number;
  } | null;
}

/**
 * 動画生成ジョブを作成
 */
export async function createVideoGenerationJob(
  payload: Record<string, unknown>
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/video/generate',
    payload
  );
  return response.data;
}

/**
 * 動画を生成（非同期ジョブとして実行し、完了まで待機）
 */
export async function generateVideoAsync(
  payload: Record<string, unknown>,
  options?: {
    onProgress?: (job: AsyncJob) => void;
    maxDuration?: number;
  }
): Promise<VideoGenerationResult> {
  const { job_id } = await createVideoGenerationJob(payload);
  const job = await waitForJobCompletion<VideoGenerationResult>(job_id, {
    onProgress: options?.onProgress,
    maxDuration: options?.maxDuration ?? 10 * 60 * 1000, // 動画生成は最大10分
  });
  return job.result_data;
}

// ============================================
// ブランドカット動画生成
// ============================================

export interface BrandCutGenerationParams {
  brand_id: string;
  logo_ids: string[];
  /** 作り方。'video' = 動画生成 AI (既定・従来) / 'motion' = モーショングラフィックス
   *  (Claude が Remotion コードを書いて描く。動画モデル・解像度・音声は持たず定額。
   *  docs/features/brand-cut-motion-graphics-plan.md) */
  engine?: 'video' | 'motion';
  image_text?: string;
  brand_name?: string;
  tagline?: string;
  description?: string;
  // ブランドカラー上書き (空文字 = クリア)。送ればその値で上書きされる
  brand_main_color?: string;
  brand_sub_color?: string;
  brand_accent_color?: string;
  duration_seconds?: number;
  video_model?: string;
  resolution?: string;
  aspect_ratio?: string;
  generate_audio?: boolean;
  /** モーションのみ: 効果音 'none' (既定) / 'auto' (AI が節目に置く。音源は都度生成) */
  sound_effects?: 'none' | 'auto';
  /** モーションのみ: ブランドに登録済みのサウンドロゴ素材 ID (ロゴが決まる瞬間に鳴らす) */
  sound_logo_material_id?: string;
  /** モーションのみ: シナリオのブランドカットのシーン id。全編モーションの計画と照合して版を記録する
   *  (docs/features/brand-cut-motion-graphics-plan.md §10.5)。このとき duration_seconds は計画の小数秒 (2〜16) */
  scene_id?: string;
  /** モーションのみ: 品質モード (§10.10.1)。'speed' = スピード / 'draft' = 標準 / 'loop' = ダメ出し (採点と修正を繰り返す・最大 3 回・時間が 2〜4 倍) */
  quality?: 'speed' | 'draft' | 'loop';
  /** モーションのみ: 初稿の AI モデル (features/motion/model・backend の許可リスト MOTION_SELECTABLE_MODELS)。省略は backend の既定 */
  model?: MotionModel;
}

export interface BrandCutGenerationResult {
  material_id: string | null;
  brand_id: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
  mime_type: string;
}

/**
 * ブランドカット動画生成ジョブを作成
 */
export async function createBrandCutGenerationJob(
  params: BrandCutGenerationParams
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/brand-cut/generate',
    params
  );
  return response.data;
}

/** 「AI で修正」(モーショングラフィックスのブランドカットのみ・定額)。元の素材は残り、修正版が新しいブランドカットになる */
export interface BrandCutReviseParams {
  material_id: string;
  /** 修正の指示 (1〜1000 文字) */
  instruction: string;
}

export async function createBrandCutReviseJob(params: BrandCutReviseParams): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>('/jobs/brand-cut/revise', params);
  return response.data;
}

/** シーンのモーショングラフィックス生成 (video_input_mode='motion' のシーン・定額。
 *  docs/features/brand-cut-motion-graphics-plan.md §10.4)。完了すると scenes.video_material_id に付く。 */
export async function createSceneMotionJob(params: {
  scene_id: string;
  /** 品質モード (§10.10.1)。'speed' = スピード / 'draft' = 標準 / 'loop' = ダメ出し (採点と修正を繰り返す)。省略時は backend の既定 */
  quality?: 'speed' | 'draft' | 'loop';
  /** 絵コンテ (production_mode='storyboard') のときだけ意味を持つ: モーション MP4 の音声 (効果音) を元の音声に重ねる
   *  (既定 false = 上書きは映像のみ。docs/features/brand-cut-motion-graphics-plan.md §10.13 6. / §10.13.1 契約 F) */
  mix_sound?: boolean;
  /** 絵コンテのときだけ意味を持つ: 上書き区間でも絵コンテのテロップを出す (既定 false = 隠す) */
  show_telop?: boolean;
  /** モーションのみ: 初稿の AI モデル (features/motion/model・backend の許可リスト MOTION_SELECTABLE_MODELS)。省略は backend の既定 */
  model?: MotionModel;
}): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>('/jobs/scene/motion', params);
  return response.data;
}

/**
 * プロンプト入力モードのモーショングラフィックス (レイヤー素材を動かす・定額) のジョブを作る
 * (docs/features/motion-graphics-mode-plan.md §5.4・§6.1・ジョブ種別 generate_motion_layered)。
 * body は features/motion/layeredGenerate.ts の buildMotionLayeredRequest で組み立てる。
 * 進捗・候補・停止はほかのモーションのジョブと同じ (features/motion/jobs.ts)。完了の result_data は
 * 動画 AI のジョブと同じキー (material_id / video_url / thumbnail_url)。
 */
export async function createMotionLayeredJob(params: {
  material_id: string;
  prompt: string;
  duration_seconds: number;
  aspect_ratio: string;
  sound_effects: 'none' | 'auto';
  end_state: 'composite' | 'free';
  locked_layer_indexes: number[];
  layer_names?: Record<string, string>;
  /** ライト 'speed' / スタンダード 'draft' / ダメ出し 'loop' (2026-10-02 からレイヤー素材もライト可) */
  quality?: 'speed' | 'draft' | 'loop';
  /** モーションのみ: 初稿の AI モデル (features/motion/model・backend の許可リスト MOTION_SELECTABLE_MODELS)。省略は backend の既定 */
  model?: MotionModel;
}): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>('/jobs/motion-layered/generate', params);
  return response.data;
}

/**
 * ブランドカット動画を生成（非同期ジョブとして実行し、完了まで待機）
 */
export async function generateBrandCutAsync(
  params: BrandCutGenerationParams,
  options?: {
    onProgress?: (job: AsyncJob) => void;
    maxDuration?: number;
  }
): Promise<BrandCutGenerationResult> {
  const { job_id } = await createBrandCutGenerationJob(params);
  const job = await waitForJobCompletion<BrandCutGenerationResult>(job_id, {
    onProgress: options?.onProgress,
    maxDuration: options?.maxDuration ?? 10 * 60 * 1000, // 動画生成は最大10分
  });
  return job.result_data;
}

// ============================================
// 静止画生成
// ============================================

// 静止画生成結果
export interface ImageGenerationResult {
  job_id: string;
  status: string;
  image_url: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
  material_id: string | null;
  model: string;
  prompt_text: string;
  actor_model_ids: string[];
}

/**
 * 静止画生成ジョブを作成
 */
export async function createImageGenerationJob(
  payload: Record<string, unknown>
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/image/generate',
    payload
  );
  return response.data;
}

/**
 * 静止画を生成（非同期ジョブとして実行し、完了まで待機）
 */
export async function generateImageAsync(
  payload: Record<string, unknown>,
  options?: {
    onProgress?: (job: AsyncJob) => void;
    maxDuration?: number;
  }
): Promise<ImageGenerationResult> {
  const { job_id } = await createImageGenerationJob(payload);
  const job = await waitForJobCompletion<ImageGenerationResult>(job_id, {
    onProgress: options?.onProgress,
    maxDuration: options?.maxDuration ?? 10 * 60 * 1000, // 静止画生成は最大10分
  });
  return job.result_data;
}

// ============================================
// Canvaアップロード
// ============================================

// Canvaアップロードパラメータ
export interface CanvaUploadParams {
  material_id: string;
  is_temporary?: boolean;
}

// Canvaアップロード結果
export interface CanvaUploadResult {
  edit_url?: string;
  design_id?: string;
  asset_id: string;
  material_id: string;
  is_video?: boolean;
  canva_url?: string;
  message?: string;
}

/**
 * Canvaアップロードジョブを作成
 */
export async function createCanvaUploadJob(
  params: CanvaUploadParams
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/canva/upload',
    params
  );
  return response.data;
}

/**
 * Canvaにアップロード（非同期ジョブとして実行し、完了まで待機）
 * ポップアップウィンドウを開くため、visibilityチェックをスキップ
 */
export async function uploadToCanvaAsync(
  params: CanvaUploadParams,
  options?: {
    onProgress?: (job: AsyncJob) => void;
    maxDuration?: number;
  }
): Promise<CanvaUploadResult> {
  const { job_id } = await createCanvaUploadJob(params);
  const job = await waitForJobCompletion<CanvaUploadResult>(job_id, {
    onProgress: options?.onProgress,
    maxDuration: options?.maxDuration ?? 5 * 60 * 1000, // Canvaアップロードは最大5分
    skipVisibilityCheck: true, // ポップアップウィンドウがアクティブでもポーリングを継続
  });
  return job.result_data;
}

// ============================================
// シーンプレビュー画像生成
// ============================================

export interface ScenePreviewResult {
  material_id?: string;
  image_url?: string;
  mime_type?: string;
  scene_id?: string;
  material?: Record<string, unknown>;
}

/**
 * シーンプレビュー画像生成ジョブを作成
 */
export async function createScenePreviewJob(
  payload: Record<string, unknown>
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/scene/preview',
    payload
  );
  return response.data;
}

// ── YouTube アップロード ────────────────────

export type YouTubePrivacyStatus = 'private' | 'unlisted' | 'public';

export interface YouTubeUploadResult {
  video_id: string;
  video_url: string;
  title: string;
  privacy_status: YouTubePrivacyStatus;
  channel_title: string | null;
  scenario_id?: string | null;
  material_id?: string | null;
}

export async function createYouTubeUploadJob(
  payload: {
    // scenario_id (合成) または material_id (単体素材) のいずれかを指定する
    scenario_id?: string;
    material_id?: string;
    title: string;
    description?: string;
    privacy_status: YouTubePrivacyStatus;
    tags?: string[];
    burn_telop?: boolean;
    contains_synthetic_media?: boolean;
  }
): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>(
    '/jobs/scenario/youtube-upload',
    payload
  );
  return response.data;
}
